import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'
import {
    organizationLanguageSelector, fetchMyOrganization, updateOrganizationLanguage
} from 'features/auth/authSlice'
import { _, withCapture } from 'utils'
import Button from 'components/Button'
import FullPageLoading from 'components/FullPageLoading'
import { showNotification } from '../../notifications'
import { updateOrganization } from '../graphql/mutation'
import { OrganizationContext } from '../Organization'
import OrganizationForm from '../components/OrganizationForm'

const S = {
    Container: styled.div`
        padding: 0 10px;
    `,
    FormActions: styled.div`
        width: 100%;
        display: flex;
        margin: 0 auto;
        padding: 15px 20px;
        justify-content: center;
    `,
    SaveButton: styled(Button)``,
}

const OrganizationValidation = Yup.object().shape({
    billingAddress: Yup.object().shape({
        addressLine1: Yup.string().required(),
        addressLine2: Yup.string().required(),
        postalCode: Yup.string().required(),
        city: Yup.string().required(),
        country: Yup.string().required(),
        state: Yup.string(),
    }),
    contactEmail: Yup.string(),
    contactPhone: Yup.string(),
    contactName: Yup.string(),
    contactPosition: Yup.string(),
    vatNumber: Yup.string().required(),
    cocNumber: Yup.string().required(),
    physicalAddress: Yup.object().shape({
        addressLine1: Yup.string().required(),
        addressLine2: Yup.string().required(),
        postalCode: Yup.string().required(),
        city: Yup.string().required(),
        country: Yup.string().required(),
        state: Yup.string(),
    }),
})

const Organization = () => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const { organization, showTab, refresh } = useContext(OrganizationContext)
    const organizationLanguage = useSelector(organizationLanguageSelector)

    const initValues = useMemo(() => ({
        billingAddress: {
            addressLine1: organization?.billingAddress?.addressLine1,
            addressLine2: organization?.billingAddress?.addressLine2,
            postalCode: organization?.billingAddress?.postalCode,
            city: organization?.billingAddress?.city,
            country: organization?.billingAddress?.country,
            state: organization?.billingAddress?.state,
        },
        contactEmail: organization?.contactEmail,
        contactPhone: organization?.contactPhone,
        contactName: organization?.contactName,
        contactPosition: organization?.contactPosition,
        physicalAddress: {
            addressLine1: organization?.physicalAddress?.addressLine1,
            addressLine2: organization?.physicalAddress?.addressLine2,
            postalCode: organization?.physicalAddress?.postalCode,
            city: organization?.physicalAddress?.city,
            country: organization?.physicalAddress?.country,
            state: organization?.physicalAddress?.state,
        },
        vatNumber: organization?.vatNumber,
        billingDepartment: organization?.billingDepartment,
        cocNumber: organization?.cocNumber,
        language: organization?.language
    }), [organization])

    useEffect(() => {
        !organizationLanguage && dispatch(updateOrganizationLanguage(organization.language))
        return () => {
            organizationLanguage && dispatch(updateOrganizationLanguage(undefined))
        }
    }, [dispatch, organization.language, organizationLanguage])

    const handleSubmit = useCallback(async (values, { resetForm }) => {
        setLoading(true)
        const response = await withCapture(
            async () => await updateOrganization(organization.id, { ...values })
        )()
        if (initValues.language !== values.language) {
            fetchMyOrganization()
        }
        setLoading(false)
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            resetForm({ values })
            refresh()
        }
    }, [dispatch, initValues.language, organization.id, refresh])

    return (
        <Formik
            initialValues={initValues || {}}
            onSubmit={handleSubmit}
            validationSchema={OrganizationValidation}
        >
            {(props: FormikProps<any>) => (
                <Form className="row">
                    <S.Container>
                        <OrganizationForm organization={organization} showTab={showTab} />
                        <S.FormActions>
                            <S.SaveButton
                                size="sm"
                                disabled={!props.dirty}
                                variant="primary"
                                icon="save"
                                onClick={props.handleSubmit}
                                type="submit"
                            >
                                {_('form.submit_button')}
                            </S.SaveButton>
                        </S.FormActions>
                        {loading && <FullPageLoading isAbsolute fullWidth />}
                    </S.Container>
                </Form>
            )}
        </Formik>
    )
}

export default Organization
